import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';

export default function SWRaiseConcernDetailPage() {
  const { report_uid } = useParams();
  const navigate = useNavigate();

  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    type: "success",
    message: "",
  });

  useEffect(() => {
      const loadReport = async () => {
          try {
              const res = await fetch(
                  `https://guardiancloudcare.com/backend/api/get_victim_report_detail.php?report_uid=${report_uid}`
              );

              const data = await res.json();

              if (data.success) {
                  setReport(data.report);
              } else {
                  alert(data.message);
              }
          } catch (err) {
              console.error(err);
          } finally {
              setLoading(false);
          }
      };

      loadReport();
  }, [report_uid]);

    const updateStatus = async (status: string) => {
        if (!report?.report_uid) return;

        setUpdating(true);

        try {
            const res = await fetch(
              "https://guardiancloudcare.com/backend/api/update_victim_report_status.php",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  report_uid: report.report_uid,
                  status,
                }),
              }
            );

            const data = await res.json();

            if (data.success) {
              // Update the status in the UI
              setReport((prev: any) => ({
                ...prev,
                status: data.status,
              }));

              setNotification({
                show: true,
                type: "success",
                message: data.message || "Report moved to In Review. Case Intake and Child Details were created successfully",
              });

              setTimeout(() => {
                setNotification((prev) => ({
                  ...prev,
                  show: false,
                }));
              }, 3000);

            } else {
              setNotification({
                show: true,
                type: "error",
                message: data.message || "Failed to update status.",
              });

              setTimeout(() => {
                setNotification((prev) => ({
                  ...prev,
                  show: false,
                }));
              }, 3000);
            }
        } catch (err) {
            console.error(err);
            alert('Server error while updating status');
        } finally {
            setUpdating(false);
        }
        };

  const parsePhotos = () => {
    try {
      return JSON.parse(report?.photo_evidence || '[]');
    } catch {
      return [];
    }
  };

  const getTimelineStep = () => {
    const status = (report?.status || 'pending').toLowerCase();

    if (status === 'resolved') return 4;
    if (status === 'in_progress') return 3;
    if (status === 'in_review') return 2;

    return 1;
    };

  const getStatusClass = (status: string) => {
    const s = (status || '').toLowerCase();

    if (s === 'resolved') return 'bg-green-100 text-green-700';
    if (s === 'in_progress') return 'bg-purple-100 text-purple-700';
    if (s === 'in_review') return 'bg-yellow-100 text-yellow-700';
    return 'bg-blue-100 text-blue-700';
  };

  const formatStatus = (status: string) => {
    const s = (status || '').toLowerCase();

    if (s === 'pending') return 'New';
    if (s === 'in_review') return 'In Review';
    if (s === 'in_progress') return 'In Progress';
    if (s === 'resolved') return 'Resolved';

    return status || 'New';
  };

  const getReporterName = () => {
    if (Number(report?.is_anonymous) === 1) {
        return "Anonymous";
    }

    return report?.reporter_name || "Unknown";
  };

  const getReporterContact = () => {
    if (Number(report?.is_anonymous) === 1) {
        return "Hidden";
    }

    return report?.reporter_contact || "-";
  };

  const formatAIConfidence = (value: any) => {
    const confidence = Number(value);

    if (!Number.isFinite(confidence)) {
        return 0;
    }

    if (confidence <= 1) {
      return Math.round(confidence * 100);
    }

    return Math.round(confidence);
  };

  if (loading) {
    return (
      <AppLayout title="Raise Concern" breadcrumb={['Social Worker', 'Raise Concern']}>
        <div className="bg-white rounded-2xl p-10 text-center">
          Loading report...
        </div>
      </AppLayout>
    );
  }

  if (!report) {
    return (
      <AppLayout title="Raise Concern" breadcrumb={['Social Worker', 'Raise Concern']}>
        <div className="bg-white rounded-2xl p-10 text-center">
          Report not found.
        </div>
      </AppLayout>
    );
  }

  const photos = parsePhotos();
  const videoEvidence = report?.video_evidence || '';

  return (
    <AppLayout title="Raise Concern" breadcrumb={['Raise Concern', 'All Reports', report.report_uid]}>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr,420px] gap-5">

        {/* LEFT SIDE */}
        <div className="space-y-5">

          {/* HEADER CARD */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>

                <h1 className="text-2xl font-extrabold text-[#1f2a5a]">
                  {report.incident_type}
                </h1>

                <div className="flex items-center gap-4 mt-4">
                  <span className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <i className="ri-hand-heart-line text-red-500 text-xl" />
                  </span>

                  <span className="bg-purple-50 text-purple-700 px-3 py-1 rounded-lg text-sm font-semibold">
                    Child
                  </span>

                  <div className="h-8 w-px bg-gray-200" />

                  <span className="text-sm text-gray-500">
                    Report ID: <b>{report.report_uid}</b>
                  </span>
                </div>
              </div>

              <button className="w-10 h-10 border border-gray-200 rounded-xl hover:bg-gray-50">
                <i className="ri-more-line" />
              </button>
            </div>
          </div>

          {/* REPORT DETAILS */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <h2 className="font-bold text-[#1f2a5a] mb-6 flex items-center gap-2">
              <i className="ri-file-list-3-line text-blue-500" />
              Report Details
            </h2>

            <div className="space-y-6">
              <DetailRow
                icon="ri-calendar-line"
                label="Date and Time Reported"
                value={report.created_at}
              />

              <DetailRow
                  icon="ri-group-line"
                  label="Child"
                  value={report.child_full_name}
              />

              <DetailRow
                icon="ri-eye-line"
                label="Witnesses"
                value={getReporterName()}
              />

              <DetailRow
                  icon="ri-home-4-line"
                  label="Address"
                  value={report.child_full_address}
              />

              <DetailRow
                  icon="ri-parent-line"
                  label="Father"
                  value={report.parents.father}
              />

              <DetailRow
                  icon="ri-parent-line"
                  label="Mother"
                  value={report.parents.mother}
              />

              <DetailRow
                  icon="ri-user-heart-line"
                  label="Guardian"
                  value={report.guardian.name}
              />

              <DetailRow
                  icon="ri-phone-line"
                  label="Guardian Contact"
                  value={report.guardian.contact}
              />


              <DetailRow
                  icon="ri-school-line"
                  label="School"
                  value={`${report.school.school_name}
              ${report.school.grade_level}
              ${report.school.section_name}`}
              />

              <DetailRow
                  icon="ri-heart-pulse-line"
                  label="Medical"
                  value={`${report.medical.medical_conditions}
              Medication: ${report.medical.medications}
              PWD: ${report.medical.with_disability}`}
              />

              <DetailRow
                icon="ri-file-text-line"
                label="Description"
                value={report.description}
              />

              <DetailRow
                icon="ri-shield-check-line"
                label="Immediate Action Taken"
                value="Advised the reporter that the report will be reviewed by the social worker."
              />
            </div>
                    </div>

          {/* AI CLASSIFICATION */}
          <div className="bg-white rounded-2xl border border-orange-100 p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center">
                  <i className="ri-sparkling-2-line text-orange-500 text-xl" />
                </div>

                <div>
                  <h2 className="font-bold text-[#1f2a5a]">
                    AI-Assisted Classification
                  </h2>

                  <p className="text-xs text-gray-500 mt-1">
                    Powered by {report.ai_provider || 'Groq'} •{' '}
                    {report.ai_model || 'GPT-OSS 20B'}
                  </p>
                </div>
              </div>

              {Number(report.ai_requires_validation) === 1 && (
                <span className="px-3 py-1.5 rounded-lg bg-yellow-50 text-yellow-700 text-xs font-semibold whitespace-nowrap">
                  Validation Required
                </span>
              )}
            </div>

            {report.ai_suggested_category ? (
              <div className="space-y-5">

                {/* CATEGORY */}
                <div className="border border-gray-100 rounded-xl p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs text-gray-500">
                        Suggested Category
                      </p>

                      <p className="text-lg font-bold text-[#1f2a5a] mt-1">
                        {report.ai_suggested_category}
                      </p>
                    </div>

                    <span className="text-sm font-bold text-orange-600">
                      {formatAIConfidence(
                        report.ai_category_confidence
                      )}%
                    </span>
                  </div>

                  <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-400 rounded-full transition-all"
                      style={{
                        width: `${formatAIConfidence(
                          report.ai_category_confidence
                        )}%`,
                      }}
                    />
                  </div>
                </div>

                {/* URGENCY */}
                <div className="border border-gray-100 rounded-xl p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs text-gray-500">
                        Suggested Urgency
                      </p>

                      <p className="text-lg font-bold text-[#1f2a5a] mt-1">
                        {report.ai_suggested_urgency || '-'}
                      </p>
                    </div>

                    <span className="text-sm font-bold text-orange-600">
                      {formatAIConfidence(
                        report.ai_urgency_confidence
                      )}%
                    </span>
                  </div>

                  <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-400 rounded-full transition-all"
                      style={{
                        width: `${formatAIConfidence(
                          report.ai_urgency_confidence
                        )}%`,
                      }}
                    />
                  </div>
                </div>

                {/* AI EXPLANATION */}
                <div className="rounded-xl bg-orange-50 border border-orange-100 p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-lightbulb-line text-orange-500 text-lg mt-0.5" />

                    <div>
                      <p className="text-sm font-bold text-orange-800">
                        AI Explanation
                      </p>

                      <p className="text-sm text-orange-900/80 leading-relaxed mt-1">
                        {report.ai_reason || 'No explanation provided.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* VALIDATION NOTICE */}
                <div className="flex items-start gap-3 p-4 rounded-xl bg-yellow-50 border border-yellow-200">
                  <i className="ri-shield-check-line text-yellow-600 text-lg mt-0.5" />

                  <div>
                    <p className="text-sm font-bold text-yellow-800">
                      Social Worker Validation Required
                    </p>

                    <p className="text-xs text-yellow-700 mt-1 leading-relaxed">
                      The AI classification is an assistive recommendation.
                      The Social Worker must review the report and confirm
                      the final category and urgency.
                    </p>
                  </div>
                </div>

                {/* ANALYZED TIME */}
                {report.ai_analyzed_at && (
                  <p className="text-xs text-gray-400">
                    AI analysis performed on {report.ai_analyzed_at}
                  </p>
                )}

              </div>
            ) : (
              <div className="rounded-xl bg-gray-50 border border-gray-100 p-5 text-center">
                <i className="ri-sparkling-line text-gray-400 text-2xl" />

                <p className="text-sm font-semibold text-gray-600 mt-2">
                  AI classification is not available
                </p>

                <p className="text-xs text-gray-400 mt-1">
                  This report was submitted before AI classification was enabled.
                </p>
              </div>
            )}
          </div>

          {/* PHOTOS / VIDEO EVIDENCE */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4 mb-5">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
                  <i className="ri-attachment-2 text-blue-500 text-xl" />
                </div>

                <div>
                  <h2 className="font-bold text-[#1f2a5a]">
                    Photos / Video Evidence
                  </h2>

                  <p className="text-xs text-gray-500 mt-1">
                    Supporting evidence submitted with this report
                  </p>
                </div>
              </div>

              <span className="text-sm text-gray-500">
                {photos.length} photo{photos.length !== 1 ? 's' : ''}
                {videoEvidence ? ' + 1 video' : ''}
              </span>
            </div>

            {/* PHOTOS */}
            {photos.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-[#1f2a5a] mb-3">
                  Photo Evidence
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {photos.map((photo: string) => (
                    <div
                      key={photo}
                      className="border border-gray-200 rounded-xl overflow-hidden bg-white"
                    >
                      <img
                        src={`https://guardiancloudcare.com/backend/uploads/victim_reports/${photo}`}
                        className="w-full h-40 object-cover"
                        alt="Report evidence"
                      />

                      <div className="px-3 py-2 flex items-center justify-between gap-2">
                        <p className="text-sm text-gray-600 truncate">
                          {photo}
                        </p>

                        <a
                          href={`https://guardiancloudcare.com/backend/uploads/victim_reports/${photo}`}
                          download
                          className="w-8 h-8 flex-shrink-0 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50"
                          title="Download photo"
                        >
                          <i className="ri-download-line text-gray-500" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* VIDEO */}
            {videoEvidence && (
              <div className={photos.length > 0 ? "mt-6 pt-6 border-t border-gray-100" : ""}>
                <p className="text-sm font-semibold text-[#1f2a5a] mb-3">
                  Video Evidence
                </p>

                <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
                  <video
                    controls
                    preload="metadata"
                    className="w-full max-h-[500px] bg-black"
                  >
                    <source
                      src={`https://guardiancloudcare.com/backend/uploads/victim_reports/${videoEvidence}`}
                      type="video/mp4"
                    />

                    Your browser does not support video playback.
                  </video>

                  <div className="px-4 py-3 flex items-center justify-between gap-3 bg-white">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-video-line text-red-500" />
                      </div>

                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-700">
                          Video Evidence
                        </p>

                        <p className="text-xs text-gray-400 truncate">
                          {videoEvidence}
                        </p>
                      </div>
                    </div>

                    <a
                      href={`https://guardiancloudcare.com/backend/uploads/victim_reports/${videoEvidence}`}
                      download
                      className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-2 flex-shrink-0"
                    >
                      <i className="ri-download-line" />
                      Download
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* NO EVIDENCE */}
            {photos.length === 0 && !videoEvidence && (
              <div className="rounded-xl bg-gray-50 border border-gray-100 p-6 text-center">
                <i className="ri-folder-open-line text-gray-300 text-3xl" />

                <p className="text-sm font-semibold text-gray-500 mt-2">
                  No evidence uploaded
                </p>

                <p className="text-xs text-gray-400 mt-1">
                  This report does not contain photo or video evidence.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="space-y-5">

          {/* STATUS */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <h2 className="font-bold text-[#1f2a5a] mb-4 flex items-center gap-2">
              <i className="ri-information-line text-blue-500" />
              Status
            </h2>

            <span className={`inline-block px-4 py-2 rounded-lg text-sm font-semibold ${getStatusClass(report.status)}`}>
              {formatStatus(report.status)}
            </span>

            <p className="text-sm text-gray-500 mt-4">
              {report.status === 'resolved'
                ? 'This report has been resolved.'
                : 'This report has not been reviewed yet.'}
            </p>

            <div className="mt-5">
                <select
                    disabled={updating}
                    value={report.status}
                    onChange={(e) => updateStatus(e.target.value)}
                    className="w-full bg-[#081257] text-white rounded-xl px-4 py-3 outline-none font-semibold"
                    >
                    <option value="pending">New</option>
                    <option value="in_review">In Review</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                </select>
            </div>
          </div>

          {/* REPORTED BY */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <h2 className="font-bold text-[#1f2a5a] mb-5 flex items-center gap-2">
              <i className="ri-user-line text-blue-500" />
              Reported By
            </h2>

            <InfoRow label="Name" value={getReporterName()} />
            <InfoRow label="Role" value={report.is_anonymous === '1' ? 'Anonymous' : 'Community Member'} />
            <InfoRow label="Contact" value={getReporterContact()} />
          </div>

          {/* CHILD INVOLVED */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <h2 className="font-bold text-[#1f2a5a] mb-5 flex items-center gap-2">
              <i className="ri-user-heart-line text-green-500" />
              Child Involved
            </h2>

            <InfoRow
                label="Full Name"
                value={report.child_full_name}
            />

            <InfoRow
                label="Nickname"
                value={report.child_nickname}
            />

            <InfoRow
                label="Age"
                value={report.age}
            />

            <InfoRow
                label="Birth Date"
                value={report.birth_date}
            />

            <InfoRow
                label="Gender"
                value={report.gender}
            />

            <InfoRow
                label="Nationality"
                value={report.nationality}
            />

            <InfoRow
                label="Religion"
                value={report.religion}
            />
          </div>

          {/* TIMELINE */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <h2 className="font-bold text-[#1f2a5a] mb-5 flex items-center gap-2">
                <i className="ri-time-line text-blue-500" />
                Timeline
            </h2>

            <div className="relative pl-6 space-y-6">
                <div className="absolute left-[5px] top-2 bottom-2 w-px bg-gray-200" />

                {[
                {
                    step: 1,
                    title: 'Report Submitted',
                    date: report.created_at,
                    sub: `by ${getReporterName()}`,
                },
                {
                    step: 2,
                    title: 'In Review',
                    date: getTimelineStep() >= 2 ? report.created_at : 'Pending',
                    sub: getTimelineStep() >= 2 ? 'Social worker reviewing report' : 'Waiting for review',
                },
                {
                    step: 3,
                    title: 'In Progress',
                    date: getTimelineStep() >= 3 ? report.created_at : 'Pending',
                    sub: getTimelineStep() >= 3 ? 'Case is being processed' : 'Not started',
                },
                {
                    step: 4,
                    title: 'Resolved',
                    date: getTimelineStep() >= 4 ? report.created_at : 'Pending',
                    sub: getTimelineStep() >= 4 ? 'Report completed' : 'Not resolved yet',
                },
                ].map((item) => (
                <TimelineItem
                    key={item.step}
                    active={getTimelineStep() >= item.step}
                    title={item.title}
                    date={item.date}
                    sub={item.sub}
                />
                ))}
            </div>
            </div>
          </div>
        </div>
    </AppLayout>
  );
}

function DetailRow({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}) {
  return (
    <div className="grid grid-cols-[44px,180px,1fr] gap-4 items-start">
      <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center">
        <i className={`${icon} text-green-600 text-lg`} />
      </div>

      <p className="text-sm font-bold text-[#1f2a5a] pt-2">
        {label}
      </p>

      <p className="text-sm text-gray-700 leading-relaxed pt-2 whitespace-pre-line">
        {value || '-'}
      </p>
    </div>
  );
}

function InfoRow({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="grid grid-cols-[120px,1fr] gap-4 py-2">
      <p className="text-sm text-gray-500">
        {label}
      </p>

      <p className="text-sm font-medium text-[#1f2a5a]">
        {value || '-'}
      </p>
    </div>
  );
}

function TimelineItem({
  title,
  date,
  sub,
  active = false,
}: {
  title: string;
  date: string;
  sub: string;
  active?: boolean;
}) {
  return (
    <div className="relative">
      <div
        className={`absolute -left-[26px] top-1 w-3 h-3 rounded-full ${
          active ? 'bg-blue-500' : 'bg-gray-300'
        }`}
      />
      <h3
        className={`text-sm font-bold ${
          active ? 'text-[#1f2a5a]' : 'text-gray-400'
        }`}
      >
        {title}
      </h3>

      <p className={`text-xs mt-1 ${active ? 'text-gray-500' : 'text-gray-300'}`}>
        {date}
      </p>

      <p className={`text-xs ${active ? 'text-gray-400' : 'text-gray-300'}`}>
        {sub}
      </p>
    </div>
  );
}